import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Plus, Trash2, GripVertical, HelpCircle } from "lucide-react";
import { ProbabilityInput } from "@/server/validators/probability-validator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface ValidationError {
  field: string;
  code: string;
  message: string;
  details?: any;
}

interface ProbabilityRangeEditorProps {
  probabilities: ProbabilityInput[];
  onChange: (probs: ProbabilityInput[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onValidationChange?: (hasErrors: boolean) => void;
}

/**
 * Constrói a notação matemática da faixa aplicada (regra de fronteira exclusiva).
 * Primeira faixa: "0 ≤ h ≤ {max}"
 * Intermediária: "{prevMax} < h ≤ {max}"
 * Última (aberta): "h > {prevMax}"
 */
function buildRangeNotation(index: number, sorted: ProbabilityInput[]): string {
  const current = sorted[index];
  const isFirst = index === 0;
  const isLast = current.maxHours === null || current.maxHours === undefined;

  if (isFirst && !isLast) {
    return `0 ≤ h ≤ ${current.maxHours}`;
  }

  if (isFirst && isLast) {
    return `h ≥ ${current.minHours ?? 0}`;
  }

  if (isLast) {
    const prev = sorted[index - 1];
    return `h > ${prev.maxHours}`;
  }

  const prev = sorted[index - 1];
  return `${prev.maxHours} < h ≤ ${current.maxHours}`;
}

/**
 * Gera descrição em linguagem natural da faixa.
 * Ex: "Até 1 hora" / "Acima de 1h até 2h" / "Acima de 8h"
 */
function buildRangeDescription(index: number, sorted: ProbabilityInput[]): string {
  const current = sorted[index];
  const isFirst = index === 0;
  const isLast = current.maxHours === null || current.maxHours === undefined;

  if (isFirst && !isLast) {
    return `Até ${current.maxHours}h`;
  }

  if (isFirst && isLast) {
    return `${current.minHours ?? 0}h ou mais`;
  }

  if (isLast) {
    const prev = sorted[index - 1];
    return `Acima de ${prev.maxHours}h`;
  }

  const prev = sorted[index - 1];
  return `Acima de ${prev.maxHours}h até ${current.maxHours}h`;
}

/**
 * Validação inline de faixas de probabilidade
 * Retorna erros específicos para cada linha
 */
function validateProbabilityRanges(probs: ProbabilityInput[]): Map<number, ValidationError[]> {
  const errors = new Map<number, ValidationError[]>();
  const activeProbabilities = probs.filter((p) => p.isActive !== false);

  // 1. Validar que existe pelo menos 1 ativa
  if (activeProbabilities.length === 0) {
    errors.set(-1, [
      {
        field: "probabilities",
        code: "NO_ACTIVE_PROBABILITIES",
        message: "Deve existir pelo menos 1 probabilidade ativa",
      },
    ]);
  }

  // 2. Validar cada linha individualmente
  activeProbabilities.forEach((prob, index) => {
    const lineErrors: ValidationError[] = [];

    // Code
    if (!prob.code || !prob.code.trim()) {
      lineErrors.push({
        field: "code",
        code: "EMPTY_CODE",
        message: "Code não pode ser vazio",
      });
    } else if (!/^P\d+$/.test(prob.code)) {
      lineErrors.push({
        field: "code",
        code: "INVALID_CODE_FORMAT",
        message: `Formato inválido. Use P1, P2, P3...`,
      });
    }

    // probabilityValue
    if (prob.probabilityValue === undefined || prob.probabilityValue === null) {
      lineErrors.push({
        field: "probabilityValue",
        code: "EMPTY_PROBABILITY_VALUE",
        message: "Valor não pode ser vazio",
      });
    } else if (!Number.isInteger(prob.probabilityValue)) {
      lineErrors.push({
        field: "probabilityValue",
        code: "INVALID_PROBABILITY_VALUE_TYPE",
        message: "Deve ser um número inteiro",
      });
    } else if (prob.probabilityValue <= 0) {
      lineErrors.push({
        field: "probabilityValue",
        code: "INVALID_PROBABILITY_VALUE_RANGE",
        message: "Deve ser maior que 0",
      });
    }

    // label
    if (!prob.label || !prob.label.trim()) {
      lineErrors.push({
        field: "label",
        code: "EMPTY_LABEL",
        message: "Nome não pode ser vazio",
      });
    }

    // minHours/maxHours
    const minHours = prob.minHours ?? null;
    const maxHours = prob.maxHours ?? null;

    if (minHours !== null && maxHours !== null && minHours > maxHours) {
      lineErrors.push({
        field: "hours",
        code: "INVALID_HOUR_RANGE",
        message: `Min (${minHours}h) não pode ser maior que Max (${maxHours}h)`,
      });
    }

    if (lineErrors.length > 0) {
      errors.set(index, lineErrors);
    }
  });

  // 3. Validar duplicatas
  const codes = activeProbabilities.map((p) => p.code);
  const duplicateCodes = codes.filter((code, index) => codes.indexOf(code) !== index);
  if (duplicateCodes.length > 0) {
    duplicateCodes.forEach((code) => {
      const indices = activeProbabilities
        .map((p, i) => (p.code === code ? i : -1))
        .filter((i) => i !== -1);
      indices.forEach((idx) => {
        if (!errors.has(idx)) errors.set(idx, []);
        errors.get(idx)!.push({
          field: "code",
          code: "DUPLICATE_CODE",
          message: `Code duplicado: ${code}`,
        });
      });
    });
  }

  // 4. Validar faixas contíguas (gaps/overlaps)
  const withHours = activeProbabilities
    .map((p, index) => ({
      ...p,
      index,
      hasMinHours: p.minHours !== null && p.minHours !== undefined,
      isClosed:
        p.minHours !== null &&
        p.minHours !== undefined &&
        p.maxHours !== null &&
        p.maxHours !== undefined,
      isOpen:
        p.minHours !== null &&
        p.minHours !== undefined &&
        (p.maxHours === null || p.maxHours === undefined),
    }))
    .filter((p) => p.hasMinHours)
    .sort((a, b) => (a.minHours as number) - (b.minHours as number));

  // 4a. Faixa aberta deve estar apenas na última posição
  for (let i = 0; i < withHours.length - 1; i++) {
    if (withHours[i].isOpen) {
      if (!errors.has(withHours[i].index)) errors.set(withHours[i].index, []);
      errors.get(withHours[i].index)!.push({
        field: "hours",
        code: "INVALID_PROBABILITY_OPEN_RANGE",
        message: "Faixa aberta deve estar na última posição",
      });
    }
  }

  // 4b. Validar gaps e overlaps
  // Intervalos contínuos com limite inferior EXCLUSIVO
  // Semântica: (minHours, maxHours] quando maxHours existe, (minHours, +∞) quando null
  // Regra: next.minHours === previous.maxHours (continuidade real, sem +1 artificial)
  for (let i = 0; i < withHours.length - 1; i++) {
    const current = withHours[i];
    const next = withHours[i + 1];

    if (next.isOpen) {
      if (current.isClosed) {
        const currentMax = current.maxHours as number;
        const nextMin = next.minHours as number;
        if (currentMax !== nextMin) {
          if (!errors.has(next.index)) errors.set(next.index, []);
          errors.get(next.index)!.push({
            field: "hours",
            code: "HOUR_GAP",
            message: `Descontinuidade detectada. Use ${currentMax}h para representar "acima de ${currentMax}h"`,
          });
        }
      }
      break;
    }

    if (current.isClosed && next.isClosed) {
      const currentMax = current.maxHours as number;
      const nextMin = next.minHours as number;

      // Overlap: next.minHours < previous.maxHours (sobreposição real)
      if (nextMin < currentMax) {
        if (!errors.has(next.index)) errors.set(next.index, []);
        errors.get(next.index)!.push({
          field: "hours",
          code: "HOUR_OVERLAP",
          message: `Sobreposição detectada. Use ${currentMax}h para continuar após "até ${currentMax}h"`,
        });
      }

      // Gap: next.minHours > previous.maxHours (descontinuidade)
      if (nextMin > currentMax) {
        if (!errors.has(next.index)) errors.set(next.index, []);
        errors.get(next.index)!.push({
          field: "hours",
          code: "HOUR_GAP",
          message: `Descontinuidade entre ${currentMax}h e ${nextMin}h. Use ${currentMax}h para continuar`,
        });
      }
    }
  }

  return errors;
}

export function ProbabilityRangeEditor({
  probabilities,
  onChange,
  disabled = false,
  isLoading = false,
  onValidationChange,
}: ProbabilityRangeEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const errors = useMemo(() => validateProbabilityRanges(probabilities), [probabilities]);
  const hasErrors = errors.size > 0;
  
  // Notificar componente pai sobre estado de validação
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  // Calcular notações de faixa aplicada (memoizado)
  const rangeNotations = useMemo(() => {
    const active = probabilities
      .filter((p) => p.isActive !== false)
      .filter((p) => p.minHours !== null && p.minHours !== undefined);
    const sorted = [...active].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const map = new Map<number, { notation: string; description: string }>();
    for (let i = 0; i < sorted.length; i++) {
      // Encontrar o índice original na lista de probabilidades
      const origIdx = probabilities.indexOf(sorted[i]);
      map.set(origIdx, {
        notation: buildRangeNotation(i, sorted),
        description: buildRangeDescription(i, sorted),
      });
    }
    return map;
  }, [probabilities]);

  const handleAddProbability = useCallback(() => {
    const activeProbabilities = probabilities.filter((p) => p.isActive !== false);
    const lastActive = activeProbabilities[activeProbabilities.length - 1];

    // Calcular a fronteira entre a faixa anterior e a nova.
    // PJC-70: a regra de continuidade é next.minHours === prev.maxHours (limite
    // inferior exclusivo). A aritmética anterior usava maxHours+1 / minHours-1, o
    // que gerava gap/overlap e, com faixa aberta, um maxHours negativo.
    const prevMin = lastActive?.minHours ?? 0;
    const boundary =
      lastActive?.maxHours !== null && lastActive?.maxHours !== undefined
        ? lastActive.maxHours
        : prevMin + 1;
    const newMinHours = boundary;

    // Fechar faixa anterior se estava aberta, exatamente na fronteira
    const updatedProbs = probabilities.map((p) => {
      if (p.isActive !== false && p.maxHours === null && p.minHours !== null) {
        const idx = probabilities.indexOf(p);
        if (idx === probabilities.length - 1) {
          return {
            ...p,
            maxHours: boundary,
          };
        }
      }
      return p;
    });

    // Adicionar nova faixa aberta
    const newProb: ProbabilityInput = {
      code: `P${activeProbabilities.length + 1}`,
      probabilityValue: activeProbabilities.length + 1,
      label: "",
      minHours: newMinHours,
      maxHours: null,
      sortOrder: activeProbabilities.length + 1,
      isActive: true,
    };

    onChange([...updatedProbs, newProb]);
  }, [probabilities, onChange]);

  const handleUpdateField = useCallback(
    (index: number, field: keyof ProbabilityInput, value: any) => {
      const updated = [...probabilities];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      onChange(updated);
    },
    [probabilities, onChange]
  );

  // PJC-32: sempre que a lista muda de tamanho/ordem, a linha que passa a ser
  // a ÚLTIMA ativa é exibida como "Sem limite" (ver render, isLast por posição)
  // e perde o input de Max Horas — mas se ela já tinha um Max Horas numérico
  // (por ter sido uma faixa intermediária antes), esse valor ficava preso sem
  // nenhuma forma de edição/limpeza na UI, e a validação do backend rejeitava
  // o rascunho com "Última faixa deve estar aberta". Esta função garante que a
  // nova última linha ativa sempre fique de fato aberta (maxHours = null).
  const reopenLastActiveRow = (probs: ProbabilityInput[]): ProbabilityInput[] => {
    let lastActiveIdx = -1;
    for (let i = probs.length - 1; i >= 0; i--) {
      if (probs[i].isActive !== false) {
        lastActiveIdx = i;
        break;
      }
    }
    if (lastActiveIdx === -1 || probs[lastActiveIdx].maxHours === null || probs[lastActiveIdx].maxHours === undefined) {
      return probs;
    }
    return probs.map((p, i) => (i === lastActiveIdx ? { ...p, maxHours: null } : p));
  };

  const handleRemove = useCallback(
    (index: number) => {
      const updated = reopenLastActiveRow(probabilities.filter((_, i) => i !== index));
      onChange(updated);
    },
    [probabilities, onChange]
  );

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const updated = [...probabilities];
    const [draggedItem] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, draggedItem);

    updated.forEach((p, i) => {
      p.sortOrder = i + 1;
    });

    onChange(reopenLastActiveRow(updated));
    setDraggedIndex(null);
  };

  const lastProbability = probabilities.filter((p) => p.isActive !== false).pop();
  const isLastProbabilityOpen =
    lastProbability && lastProbability.maxHours === null && lastProbability.minHours !== null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Caracterização da Hora Extra (Probabilidade)</CardTitle>
            <CardDescription>
              Defina faixas contínuas de horas. A última faixa é sempre aberta (sem limite máximo).
            </CardDescription>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="ml-2">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Regra de Fronteira Exclusiva (sem sobreposição)</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Como funciona:</h4>
                  <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                    <li>
                      <strong>Primeira faixa (P1):</strong> Inclui o valor máximo → <code className="bg-muted px-1 rounded">0 ≤ h ≤ max</code>
                    </li>
                    <li>
                      <strong>Faixas intermediárias:</strong> Excluem o limite inferior → <code className="bg-muted px-1 rounded">prevMax &lt; h ≤ max</code>
                    </li>
                    <li>
                      <strong>Última faixa (aberta):</strong> Captura tudo acima → <code className="bg-muted px-1 rounded">h &gt; prevMax</code>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Exemplo com dados atuais:</h4>
                  <div className="text-sm text-muted-foreground space-y-1 bg-muted p-3 rounded-md font-mono">
                    <div>P1 Eventual: <strong>0 ≤ h ≤ 0,5</strong> → "Até 30min"</div>
                    <div>P2 Ocasional: <strong>0,5 &lt; h ≤ 1</strong> → "Acima de 30min até 1h"</div>
                    <div>P3 Intermitente: <strong>1 &lt; h ≤ 2</strong> → "Acima de 1h até 2h"</div>
                    <div>P4 Prolongado: <strong>2 &lt; h ≤ 3</strong> → "Acima de 2h até 3h"</div>
                    <div>P5 Permanente: <strong>h &gt; 3</strong> → "Acima de 3h"</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground border-t pt-2">
                  <strong>Sem sobreposição:</strong> h=0,5 é P1, h=0,51 é P2. A coluna "Faixa Aplicada" mostra a interpretação real.
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Banner de erro geral */}
        {hasErrors && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Corrija as faixas antes de publicar: as faixas devem ser contínuas (ex: "até 8h" + "acima de 8h") e a última deve estar aberta.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabela */}
        {probabilities.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">Nenhuma faixa de probabilidade configurada</p>
            <Button onClick={handleAddProbability} disabled={disabled || isLoading}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar primeira faixa
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b">
                <tr>
                  <th className="px-4 py-2 text-left w-8"></th>
                  <th className="px-4 py-2 text-left">Código</th>
                  <th className="px-4 py-2 text-left">Nome</th>
                  <th className="px-4 py-2 text-left">Valor</th>
                  <th className="px-4 py-2 text-left">Min Horas</th>
                  <th className="px-4 py-2 text-left">Max Horas</th>
                  <th className="px-4 py-2 text-center" title="Interpretação real da faixa, sem sobreposição">
                    Faixa Aplicada
                  </th>
                  <th className="px-4 py-2 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {probabilities.map((prob, index) => {
                  const isActive = prob.isActive !== false;
                  const isLast =
                    index === probabilities.filter((p) => p.isActive !== false).length - 1;
                  const lineErrors = errors.get(index) || [];
                  const hasLineError = lineErrors.length > 0;
                  const range = rangeNotations.get(index);

                  return (
                    <tr
                      key={index}
                      draggable={isActive && !disabled}
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      className={`border-b transition-colors ${
                        hasLineError ? "bg-red-50" : draggedIndex === index ? "bg-blue-50" : ""
                      } ${!isActive ? "opacity-50" : ""}`}
                    >
                      <td className="px-4 py-2">
                        {isActive && (
                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono">{prob.code || "-"}</td>
                      <td className="px-4 py-2">
                        <Input
                          value={prob.label || ""}
                          onChange={(e) => handleUpdateField(index, "label", e.target.value)}
                          placeholder="Ex: Eventual"
                          disabled={disabled || isLoading || !isActive}
                          className={lineErrors.some((e) => e.field === "label") ? "border-red-500" : ""}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          value={prob.probabilityValue || ""}
                          onChange={(e) =>
                            handleUpdateField(index, "probabilityValue", parseInt(e.target.value) || null)
                          }
                          placeholder="1"
                          disabled={disabled || isLoading || !isActive}
                          className={lineErrors.some((e) => e.field === "probabilityValue") ? "border-red-500" : ""}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          step="0.25"
                          min="0"
                          value={prob.minHours ?? ""}
                          onChange={(e) =>
                            // PJC-70: parseFloat — as faixas de hora extra usam frações
                            // (0,5h). Com parseInt, digitar 0,5 virava 0 em silêncio.
                            handleUpdateField(index, "minHours", e.target.value === "" ? null : parseFloat(e.target.value))
                          }
                          placeholder="0"
                          disabled={disabled || isLoading || !isActive}
                          className={lineErrors.some((e) => e.field === "hours") ? "border-red-500" : ""}
                        />
                      </td>
                      <td className="px-4 py-2">
                        {isLast ? (
                          <div className="text-sm text-muted-foreground">
                            Sem limite
                            {prob.minHours !== null && prob.minHours !== undefined && (
                              <div className="text-xs">≥ {prob.minHours}h</div>
                            )}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            step="0.25"
                            min="0"
                            value={prob.maxHours ?? ""}
                            onChange={(e) =>
                              // PJC-70: parseFloat, ver comentário em minHours
                              handleUpdateField(index, "maxHours", e.target.value === "" ? null : parseFloat(e.target.value))
                            }
                            placeholder="8"
                            disabled={disabled || isLoading || !isActive}
                            className={lineErrors.some((e) => e.field === "hours") ? "border-red-500" : ""}
                          />
                        )}
                      </td>
                      {/* Coluna Faixa Aplicada — read-only, calculada automaticamente */}
                      <td className="px-4 py-2 text-center">
                        {range ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                              {range.notation}
                            </code>
                            <span className="text-[11px] text-muted-foreground">
                              {range.description}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(index)}
                          disabled={disabled || isLoading || !isActive}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Mensagens de erro inline */}
        {Array.from(errors.entries()).map(([idx, lineErrors]) => (
          <div key={idx} className="space-y-2">
            {lineErrors.map((error, errorIdx) => (
              <Alert key={errorIdx} variant="destructive" className="text-sm">
                <AlertTriangle className="h-3 w-3" />
                <AlertDescription>{error.message}</AlertDescription>
              </Alert>
            ))}
          </div>
        ))}

        {/* Botão Adicionar */}
        {probabilities.length > 0 && (
          <Button
            onClick={handleAddProbability}
            disabled={disabled || isLoading || !isLastProbabilityOpen}
            variant="outline"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar faixa
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
